import {
  PatientEncounter,
} from '../../../../patient/components/models/patient-encounter';
import {GuestToken} from '../../../../security/services/SSO';
import {
  ApplicationStatusState,
  GetError,
} from '../../../../security/model/application-status';
import React, {useState} from 'react';
import HttpInterceptor from '../../../../shared/Http/http-interceptor';
import {patientService} from '../../../../patient/services/patient-service';
import {
  specialistClientService,
} from '../../../../implementation-specialist/services/implementation-client-service';
import {AuthorizationDocumentStatus} from './payment-program-enrollment-page';
import {Workflow} from '../../../../shared/model/client';
import {getDynamicContent} from '../no-sig-dynamic-content';
import {ApprovalView} from '../approval-view';
import {getMethodApprovedFromSearch} from '../get-method-approved';
import {
  commentsService
} from '@finpay-development/shared-platform/lib/esm/admin/services/comments-service';

interface EnrollmentApprovalViewProps {
  patientEncounter: PatientEncounter;
  selectedAuthDocStatus: AuthorizationDocumentStatus | null;
  guestToken: GuestToken | null;
  setMessage: (msg: ApplicationStatusState) => void;
  authDocPdfBlob: Blob | null
}

export interface ClientDetails {
  surveyUrl: string
  clientName: string
  email: string
  phoneNumber: string
}

export type ApprovalState = {
  isCheckboxChecked: boolean;
  isAuthDocViewed: boolean;
};

export const EnrollmentApprovalView = (props: EnrollmentApprovalViewProps) => {

  const {
    patientEncounter,
    guestToken,
    selectedAuthDocStatus,
    setMessage,
    authDocPdfBlob
  } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [isEnrollmentThankYou, setIsEnrollmentThankYou] = useState(false);
  const [clientDetails, setClientDetails] = useState<ClientDetails>({
    surveyUrl: '',
    clientName: '',
    email: '',
    phoneNumber: '',
  });

  const handleApproveBtnOnClick = async () => {

    HttpInterceptor(false, guestToken!, true);

    const peStatus = getPEStatusForAuthDocSigned({
        isACH: patientEncounter?.patientPaymentProgram?.[0].isACH,
        isPaidInFull: patientEncounter?.patientPaymentProgram?.[0].isPaidInFull,
        isACHVerified: patientEncounter?.patientPaymentProgram?.[0].isACHVerified!,
        pfrCapturedBalance: patientEncounter?.patientPaymentProgram?.[0]?.patientPaymentSchedule.pfrCapturedBalance!
    });

    const resolvedPromises = await Promise.all([
      processAuthDocApproval({
        authorizationDocumentStatusId: selectedAuthDocStatus?.authorizationDocumentStatusId!,
        peStatus,
        patientEncounterId: patientEncounter.patientEncounterId,
        patientId: patientEncounter.patientId,
        isPaidInFull: !!patientEncounter?.patientPaymentProgram?.[0]?.isPaidInFull
      }),
      //getting survey url path
      specialistClientService.getClient(patientEncounter.clientId),
    ]);

    for (let i = 0; i < resolvedPromises.length; i++) {
      if (resolvedPromises?.[i]?.hasErrors) {
        setMessage(GetError(
            'A problem occurred while approving your payment program. Please reach out to a FinPay administrator.'));
        return;
      }
    }

    const fetchedClientDetail = resolvedPromises[1]?.entity?.fullClient;

    if(
        fetchedClientDetail.clientCrm.length > 0 &&
        fetchedClientDetail.clientCrm?.[0]?.crmType?.crmTypeSlug &&
        patientEncounter.clientsPatientIOCId
    ){

      const integrationUpdateResponse = await patientService.integrationUpdate({
        patientEncounterId: patientEncounter.patientEncounterId,
        patientId: patientEncounter.patientId,
        crmTypeSlug: fetchedClientDetail.clientCrm?.[0].crmType.crmTypeSlug
      })

      if(integrationUpdateResponse.hasErrors){
        console.error("Error occurred during real time update")
      }

    }

    setIsEnrollmentThankYou(true);
    setClientDetails({
      ...clientDetails,
      surveyUrl: fetchedClientDetail?.surveyUrl,
      clientName: fetchedClientDetail?.clientName,
      email: fetchedClientDetail?.primaryContact?.email,
      phoneNumber: fetchedClientDetail?.primaryContact?.primPhoneNum,
    });

  };

  const dynamicContent = getDynamicContent({
    typeOfDoc: selectedAuthDocStatus?.authorizationDocuments?.typeOfDoc!,
    patientEncounter,
    modalState: {
      state: modalOpen,
      setState: setModalOpen
    },
    pdfBlob: authDocPdfBlob
  })

  return (
      <ApprovalView
          pageContent={{
            title: `${patientEncounter?.client?.clientName} at ${patientEncounter?.facilityName} Payment Agreement`,
            body: {
              intro: 'We are happy you found an agreeable payment program that meets your budget. Please read the attached forms, terms, and conditions before agreeing to them.',
              details: dynamicContent.programDetails!,
              programAgreementDetails: dynamicContent.programAgreementDetails,
              explanation: ' Any pending payments paid will be credited to your estimated remaining balance.',
              instructions: 'to review the forms and terms and conditions of this Payment Program Agreement, which includes the following:',
              checkboxLabel: "I accept the terms of this Payment Program"
            },
            footerNotes: [
                'Your actual PFR may change depending on any insurance coverages & the services you receive.'
            ]
          }}
          docModal={dynamicContent.docModal}
          displayThankYouView={isEnrollmentThankYou}
          clientDetails={clientDetails}
          modalOpenState={[modalOpen, setModalOpen]}
          setMessage={setMessage}
          handleApprovedBtnOnClick={handleApproveBtnOnClick}
          thankYouViewConfig={{
            thankYouText: 'Thank you for enrolling!'
          }}
      />
  );

};

type PEStatus = {
  finClearanceStatus: string
  workflow: Omit<Workflow, 'workflowName'>
} | null

const getPEStatusForAuthDocSigned = ({
                       isACH,
                       isPaidInFull,
                       isACHVerified,
                       pfrCapturedBalance
}:
                         {
                             isACH: boolean,
                             isPaidInFull: boolean,
                             isACHVerified: boolean
                             pfrCapturedBalance: number
                         }): PEStatus => {

  switch (true) {

    case !isACH && isPaidInFull && pfrCapturedBalance === 0:
    case isACH && isPaidInFull && isACHVerified && pfrCapturedBalance === 0:
      return {
        finClearanceStatus: 'Paid',
        workflow: {
          'workflowId': 3,
          'workflowStatus': {
            'workflowStatusId': 16,
            'workflowStatusDesc': 'Paid',
          },
          'workflowSubStatus': {
            'workflowSubStatusId': 23,
            'workflowSubStatusDesc': 'None',
          },
        },
      };
    case !isACH && !isPaidInFull:
    case !isACH && isPaidInFull && (pfrCapturedBalance > 0 || pfrCapturedBalance === null):
    case isACH && isACHVerified && !isPaidInFull:
    case isACH && isACHVerified && isPaidInFull && (pfrCapturedBalance > 0 || pfrCapturedBalance === null):
      return {
        finClearanceStatus: 'Converted',
        workflow: {
          'workflowId': 3,
          'workflowStatus': {
            'workflowStatusId': 12,
            'workflowStatusDesc': 'Converted',
          },
          'workflowSubStatus': {
            'workflowSubStatusId': 23,
            'workflowSubStatusDesc': 'None',
          },
        },
      };
    case isACH && !isACHVerified:
      return {
        finClearanceStatus: 'Converted, Pending - Awaiting ACH Verification',
        workflow: {
          'workflowId': 3,
          'workflowStatus': {
            'workflowStatusId': 17,
            'workflowStatusDesc': 'Converted, Pending',
          },
          'workflowSubStatus': {
            'workflowSubStatusId': 34,
            'workflowSubStatusDesc': 'Awaiting ACH Verification',
          },
        },
      };
    default:
      return null;
  }

};

const processAuthDocApproval = async (params : {
  authorizationDocumentStatusId: number
  peStatus: PEStatus
  patientEncounterId: number
  patientId: number
  isPaidInFull: boolean
}) => {

  const {authorizationDocumentStatusId, peStatus, patientEncounterId, patientId, isPaidInFull} = params;
  const methodApproved = getMethodApprovedFromSearch();

  const authDocResponse = await patientService.updateAuthorizationDocumentStatus(
      {
        dataToSave: {
          documentStatus: 'signed',
          dateApproved: new Date().toISOString(),
          ...methodApproved && {methodApproved},
        },
        authorizationDocumentStatusId: authorizationDocumentStatusId,
      });

  if (authDocResponse?.hasErrors) {
    return authDocResponse;
  }

  const docCreationResponse =  await patientService.createNoSigHelloSignDoc({
    authorizationDocumentStatusId,
  });

  if (docCreationResponse?.hasErrors || !peStatus) {

    if(docCreationResponse?.hasErrors) {
      await patientService.updateAuthorizationDocumentStatus(
          {
            dataToSave: {
              documentStatus: 'sent',   //reverting status back to sent to avoid conflicts
              dateApproved: null
            },
            authorizationDocumentStatusId: authorizationDocumentStatusId,
          });
    }

    return docCreationResponse;
  }

 const resolvedPromises = await Promise.all([
    patientService.saveStatus({
      workFlow: peStatus.workflow,
      encounterId: patientEncounterId,
      patientId,
    }),
    patientService.saveIOCWithPartialObject({
      finClearanceStatus: peStatus.finClearanceStatus,
      finClearanceStatusDt: new Date().toISOString(),
    }, patientEncounterId),
   await commentsService.createPatientNote({
     patientId,
     patientEncounterId,
     noteText: `${isPaidInFull ? "Single Pay Agreement" : "Installment Agreement"} has been approved.`,
     noteDt: new Date().toISOString()
   })
  ])

  for (let i = 0; i < resolvedPromises.length; i++) {
    if (resolvedPromises[i].hasErrors) {
      return resolvedPromises[i];
    }
  }

};
